import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { FormThemeProvider } from '@minetrace/ui/forms'
import Sidebar, { RAIL_W, RAIL_W_COLLAPSED } from './Sidebar'
import Topbar from './Topbar'
import CommandPalette from './CommandPalette'
import ConsentModal from '../ConsentModal'
import { useMenu } from '../../auth/PermissionContext'
import type { MeMenuItem } from '../../api/services/me.service'
import { accentFor, brand, mineral, statusColors, themeColors } from '../../theme'
import { CONSENT_PATHS, findActive, flattenLeaves, pushRecent } from './nav'

const formColors = {
  isDark: false as const,
  text: themeColors.text,
  textSecondary: themeColors.textSecondary,
  textMuted: themeColors.textMuted,
  textDimmed: themeColors.textDimmed,
  bgInput: themeColors.bgHover,
  bgCard: themeColors.bgCard,
  bgCardSolid: themeColors.bgCardSolid,
  bgHover: themeColors.bgHover,
  border: themeColors.border,
  accent: brand.green,
  // Sin degradado: el botón primario es un verde plano.
  accentGradient: brand.primary,
  error: statusColors.error,
  success: statusColors.success,
}

const COLLAPSE_KEY = 'kuntur.rail.collapsed'

export default function AppLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menu = useMenu()
  const [drawer, setDrawer] = useState(false)
  const [search, setSearch] = useState(false)
  const [consentTarget, setConsentTarget] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
  })

  const leaves = useMemo(() => flattenLeaves(menu), [menu])
  const acc = accentFor(findActive(leaves, pathname)?.section.code)

  const toggleCollapsed = () => {
    setCollapsed(c => {
      try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1') } catch { /* sin persistencia */ }
      return !c
    })
  }

  const onNavigate = useCallback((item: MeMenuItem) => {
    if (!item.route) return
    setDrawer(false)
    if (CONSENT_PATHS.includes(item.route)) {
      setConsentTarget(item.route)
      return
    }
    pushRecent(item.route)
    navigate(item.route)
  }, [navigate])

  // Ctrl/⌘ + K abre la paleta desde cualquier pantalla.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearch(s => !s)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const railW = collapsed ? RAIL_W_COLLAPSED : RAIL_W

  return (
    <Box
      minH="100vh"
      bg={mineral.canvas}
      // El mineral de la sección viaja como variable CSS: PageHeader, DataList y
      // botones de @minetrace/ui lo leen sin que cada pantalla lo pase por props.
      style={{ ['--k-accent' as string]: acc.color, ['--k-accent-deep' as string]: acc.deep, ['--k-accent-soft' as string]: acc.soft }}
    >
      {/* Riel (escritorio) */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <motion.aside
          initial={false}
          animate={{ width: railW }}
          transition={{ type: 'spring', stiffness: 400, damping: 40 }}
          style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50, borderRight: '1px solid rgba(0,0,0,0.2)' }}
        >
          <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} onNavigate={onNavigate} onOpenSearch={() => setSearch(true)} />
        </motion.aside>
      </Box>

      {/* Drawer (móvil) */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div key="scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(42,30,20,0.5)', backdropFilter: 'blur(3px)', zIndex: 80 }} />
            <motion.div key="drawer" initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
              style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 'min(86vw, 320px)', zIndex: 81 }}>
              <Sidebar variant="drawer" collapsed={false} onNavigate={onNavigate} onOpenSearch={() => { setDrawer(false); setSearch(true) }} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Box pl={{ base: 0, lg: `${railW}px` }} transition="padding-left .38s cubic-bezier(.2,.7,.2,1)">
        <Topbar onOpenMenu={() => setDrawer(true)} onOpenSearch={() => setSearch(true)} />
        <Box as="main" position="relative">
          {/* Halo del mineral de la sección: cambia de color al cambiar de módulo */}
          <Box position="absolute" top={0} left={0} right={0} h="320px" pointerEvents="none" zIndex={0}
            transition="background 0.6s ease"
            style={{ background: `radial-gradient(70% 100% at 85% -10%, ${acc.color}1f 0%, transparent 70%)` }} />
          {/* Entrada de página: sólo entra (sin esperar a que la anterior salga), así navegar es inmediato. */}
            <motion.div
              key={pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
              style={{ position: 'relative', zIndex: 1 }}
            >
              <Box px={{ base: 4, md: 8 }} py={{ base: 5, md: 8 }} maxW="1640px" mx="auto">
                <FormThemeProvider colors={formColors}>
                  <Outlet />
                </FormThemeProvider>
              </Box>
            </motion.div>
        </Box>
      </Box>

      <CommandPalette open={search} onClose={() => setSearch(false)} onNavigate={onNavigate} />

      <ConsentModal
        open={consentTarget !== null}
        onClose={() => setConsentTarget(null)}
        onAccept={() => {
          if (consentTarget) { pushRecent(consentTarget); navigate(consentTarget) }
          setConsentTarget(null)
        }}
      />
    </Box>
  )
}
